import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { Underwriter } from '../models/Underwriter';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';

@Injectable({
  providedIn: 'root'
})
export class UnderwritersService {

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar
  ) { }

  async getUnderwriters(): Promise<Underwriter[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/underwriters/query`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving underwriters: ${JSON.stringify(e)}`);
    }
  }

  async createUnderwriter(underwriter: any): Promise<Underwriter> {
    try {
      const payload: Underwriter = {
        id: underwriter.id,
        account_id: 0,
        name: underwriter.name,
        description: underwriter.description,
        is_active: true
      }

      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/underwriters`, payload));
      if (!response) {
        return Promise.reject(`Create underwriters response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating underwriters: ${e}`);
    }
  }

  async updateUnderwriter(underwriterId: number, underwriter: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/underwriters/${underwriterId}`, underwriter));
      if (!response) {
        return Promise.reject(`Update underwriter response null ${underwriterId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating underwriter ${underwriterId}: ${e}`);
    }
  }


  async getPolicyUnderwriters(): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policies/underwriters/query`));

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving Policy Underwriters: ${e}`);
    }
  }

  async getPolicyUnderwritersByPolicyCoverageId(policyCoverageId: number): Promise<any[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policies/underwriters/query?policy_coverage_id=${policyCoverageId}&status_id=1`));

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving Policy Underwriters for Policy Coverage ID ${policyCoverageId}: ${e}`);
    }
  }

  async createPolicyUnderwriter(payload: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/policies/underwriters`, payload));
      if (!response) {
        return Promise.reject(`Create policy underwriter response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating policy underwriter: ${e}`);
    }
  }

  async updatePolicyUnderwriter(underwriterId: any, payload: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policies/underwriters/${underwriterId}`, payload));
      if (!response) {
        return Promise.reject(`Update policy underwriter response null ${underwriterId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating policy underwriter ${underwriterId}: ${e}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }

  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Underwriter: ${errorMessage}`, 'OK', { duration: 4000 });
  }
}